import type { ReactNode } from 'react';
import * as RTooltip from '@radix-ui/react-tooltip';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { cx } from '@/lib/cx';
import { initials } from '@/lib/format';

/* ---------------------------------------------------------------- Avatar */

const AVATAR_HUES = [214, 142, 36, 4, 262, 188, 330, 96];
function hueFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length]!;
}

export function Avatar({ name, src, size = 28, className, title }: { name?: string | null; src?: string | null; size?: number; className?: string; title?: string }) {
  const n = name ?? '';
  return (
    <span
      className={cx('avatar', className)}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.38)), ['--avatar-hue' as string]: hueFor(n) }}
      title={title ?? n}
      aria-hidden={!title}
      role={title ? 'img' : undefined}
      aria-label={title}
    >
      {src ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : initials(n)}
    </span>
  );
}

export function AvatarStack({ names, max = 3, size = 24 }: { names: string[]; max?: number; size?: number }) {
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  return (
    <span className="avatar-stack" aria-label={names.join(', ')} role="img">
      {shown.map((n, i) => (
        <Avatar key={`${n}-${i}`} name={n} size={size} />
      ))}
      {rest > 0 && (
        <span className="avatar avatar-more" style={{ width: size, height: size }} aria-hidden>
          +{rest}
        </span>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------- Tooltip */

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RTooltip.Provider delayDuration={350} skipDelayDuration={150}>
      {children}
    </RTooltip.Provider>
  );
}

/** Supplementary hint only — never put essential information in a tooltip. */
export function Tooltip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <RTooltip.Root>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content side={side} sideOffset={6} className="tooltip" collisionPadding={8}>
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}

/* ---------------------------------------------------------------- Kbd */

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

/* ---------------------------------------------------------------- Skeleton */

export function Skeleton({ width, height = 14, radius, className, circle }: { width?: number | string; height?: number | string; radius?: number; className?: string; circle?: boolean }) {
  return <span className={cx('skeleton', className)} aria-hidden style={{ width: width ?? '100%', height, borderRadius: circle ? '50%' : radius }} />;
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <span className="skeleton-text" aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '62%' : '100%'} />
      ))}
    </span>
  );
}

/** Row-shaped skeleton for tables/lists. */
export function SkeletonRows({ rows = 6, columns = 4, label = 'Loading' }: { rows?: number; columns?: number; label?: string }) {
  return (
    <div className="skeleton-rows" role="status" aria-live="polite" aria-label={label}>
      {Array.from({ length: rows }, (_, r) => (
        <div className="skeleton-row" key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} width={c === 0 ? '70%' : `${40 + ((r * 7 + c * 13) % 45)}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- InlineAlert */

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';
const ALERT_ICON = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: CircleAlert } as const;

export interface InlineAlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
  /** Announce politely (default) or assertively for errors that block progress. */
  live?: 'polite' | 'assertive' | 'off';
  icon?: ReactNode;
}

export function InlineAlert({ tone = 'info', title, children, action, onDismiss, className, live = 'polite', icon }: InlineAlertProps) {
  const Icon = ALERT_ICON[tone];
  return (
    <div className={cx('alert', `tone-${tone === 'danger' ? 'danger' : tone}`, className)} role={tone === 'danger' ? 'alert' : 'status'} aria-live={live}>
      <span className="alert-icon" aria-hidden>{icon ?? <Icon size={16} />}</span>
      <div className="alert-body">
        {title && <p className="alert-title">{title}</p>}
        {children && <div className="alert-text">{children}</div>}
      </div>
      {action && <div className="alert-action">{action}</div>}
      {onDismiss && (
        <button type="button" className="alert-dismiss" aria-label="Dismiss" onClick={onDismiss}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- KeyValue */

export interface KeyValueItem {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
}

export function KeyValueList({ items, columns = 1, className }: { items: KeyValueItem[]; columns?: 1 | 2 | 3; className?: string }) {
  return (
    <dl className={cx('kv', `kv-cols-${columns}`, className)}>
      {items.map((it, i) => (
        <div className="kv-item" key={i}>
          <dt className="kv-label">{it.label}</dt>
          <dd className="kv-value">
            {it.value === null || it.value === undefined || it.value === '' ? <span className="subtle">—</span> : it.value}
            {it.hint && <span className="kv-hint">{it.hint}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ---------------------------------------------------------------- Stat */

/** A real number with a label. Never use for placeholder or invented metrics. */
export function Stat({ label, value, hint, tone, icon }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'danger' | 'warning' | 'success'; icon?: ReactNode }) {
  return (
    <div className={cx('stat', tone && `stat-${tone}`)}>
      <span className="stat-label">
        {icon && <span aria-hidden className="stat-icon">{icon}</span>}
        {label}
      </span>
      <span className="stat-value tabular">{value}</span>
      {hint && <span className="stat-hint">{hint}</span>}
    </div>
  );
}

/* ---------------------------------------------------------------- Panel / Section */

export function Panel({ title, description, actions, children, className, padded = true, as: As = 'section', id }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; padded?: boolean; as?: 'section' | 'div'; id?: string }) {
  return (
    <As className={cx('panel', className)} aria-labelledby={title && id ? `${id}-title` : undefined} id={id}>
      {(title || actions) && (
        <header className="panel-head">
          <div className="panel-head-text">
            {title && <h2 className="panel-title" id={id ? `${id}-title` : undefined}>{title}</h2>}
            {description && <p className="panel-desc">{description}</p>}
          </div>
          {actions && <div className="panel-actions">{actions}</div>}
        </header>
      )}
      <div className={cx('panel-body', !padded && 'panel-body-flush')}>{children}</div>
    </As>
  );
}

/** Sticky bottom action bar for long forms (save / cancel stay reachable). */
export function StickyActionBar({ children, status }: { children: ReactNode; status?: ReactNode }) {
  return (
    <div className="sticky-actions" role="region" aria-label="Actions">
      <div className="sticky-actions-status">{status}</div>
      <div className="sticky-actions-buttons">{children}</div>
    </div>
  );
}

export function Divider({ label }: { label?: string }) {
  return label ? (
    <div className="divider-label" role="separator">
      <span>{label}</span>
    </div>
  ) : (
    <hr className="divider" />
  );
}
